import Arrow from "./Arrow";
import Phaser from "phaser";
import Monster from "./Monster";
import Bow, { Bows } from "./Bow";
import BarGui from "./GUI/BarGUI";
import ProgressCircle from "./GUI/ProgressCircle";
import CharacterAttributes from "./Character/CharacterAttributes";
import { DamageMath, CalculatedDamage } from "./Math/DamageMath";
import BowMath from "./Math/BowMath";
import CharacterStorage from "./Storage/CharacterStorage";
import AttributeMath from "./Math/AttributeMath";
import ExperiencePoints from "./Character/ExperiencePoints";
import StoryStorage from "./Storage/StoryStorage";
import Constants from "./Math/Constants";
import SkillManager from "./Skills/SkillManager";
import { IManaConsumer } from "./Skills/IManaConsumer";
import { IArrowCaster } from "./Skills/IArrowCaster";
import ArrowContainer from "./Arrow/ArrowContainer";
import ArrowCaster from "./Arrow/ArrowCaster";
import MathHelpers from "./Math/MathHelpers";
import FloatingTexts from "./GUI/FloatText";

export default class ArcherCharacter implements IManaConsumer {
  private mHasCreated: boolean = false;
  private sprite: Phaser.Types.Physics.Arcade.SpriteWithStaticBody;
  private mBowSprite;
  private mHealthBar: BarGui;
  private mManaBar: BarGui;
  private mPowerArc: ProgressCircle;

  private mBow: Bow;
  private mArrowContainer: ArrowContainer;
  private mArrowCaster: ArrowCaster;
  private mPullArrowStartTime: Date | null;

  private mCharacterAttribs: CharacterAttributes;
  private mExperiencePoints: ExperiencePoints;
  private mCharacterStorage: CharacterStorage;
  private mStoryStorage: StoryStorage;
  private mSkillManager: SkillManager;

  private mAttachedScene: Phaser.Scene;
  private mFloatingTexts: FloatingTexts;
  private mAimPointer: Phaser.Input.Pointer | null = null;

  private mOnArrowHitCallback: (arrow: Arrow, monster: Monster, scene: Phaser.Scene) => void;
  private mOnDamageCallback: (scene: Phaser.Scene, monster: Monster, damage: number) => void;
  private mOnLoseCallback: ((scene: Phaser.Scene) => void) | null;

  constructor(
    scene: Phaser.Scene,
    skillManager: SkillManager,
    charStorage: CharacterStorage,
    storyStorage: StoryStorage,
    arrowHitCallback: (arrow: Arrow, monster: Monster, scene: Phaser.Scene) => void,
    onDamageCallback: (scene: Phaser.Scene, monster: Monster, damage: number) => void,
    onLoseCallback: (scene: Phaser.Scene) => void
  ) {
    this.mAttachedScene = scene;
    this.mArrowContainer = new ArrowContainer();
    this.mOnArrowHitCallback = arrowHitCallback;
    this.mOnDamageCallback = onDamageCallback;
    this.mOnLoseCallback = onLoseCallback;
    this.mBow = Bows.BasicBow;
    this.mCharacterAttribs = new CharacterAttributes();
    this.mCharacterStorage = charStorage;
    this.mStoryStorage = storyStorage;
    this.mSkillManager = skillManager;
  }

  public PreLoad() {
    let characterConfig: Phaser.Types.Loader.FileTypes.ImageFrameConfig = {
      frameWidth: 312,
      frameHeight: 258,
    };

    this.mAttachedScene.load.spritesheet(
      "character",
      "images/character5.png",
      characterConfig
    );

    this.mAttachedScene.load.image("arrow", "images/arrow.png");

    Bows.PreloadAllBows(this.mAttachedScene);
    const floorY = this.mAttachedScene.scale.height;
    const yLocation = floorY - Constants.YLocation / 2;

    this.sprite = this.mAttachedScene.physics.add.staticSprite(
      80,
      yLocation,
      "character",
      0
    );

    if (this.mBowSprite != undefined || this.mBowSprite != null) {
      this.mBowSprite.destroy();
      this.mBowSprite = undefined;
    }
    this.mBowSprite = this.mAttachedScene.physics.add.staticSprite(
      80,
      yLocation,
      `bow_${this.mBow.Name}`,
      0
    );
  }

  public Create(experiencePoints: ExperiencePoints) {
    Bows.CreateAllBowsAnimation(this.mAttachedScene);

    if (!this.mHasCreated) {
      this.mAttachedScene.anims.create({
        key: `shoot_${this.mBow.Name}`,
        frames: this.mAttachedScene.anims.generateFrameNumbers("character", {
          start: 0,
          end: 2,
        }),
        repeat: 0,
        duration:
          this.mBow.TimeToFullPower.getSeconds() * 1000 +
          this.mBow.TimeToFullPower.getMilliseconds(),
      });

      this.mAttachedScene.anims.create({
        key: `release`,
        frames: this.mAttachedScene.anims.generateFrameNumbers("character", {
          start: 3,
          end: 5,
        }),
        repeat: 0,
        duration: 200,
      });

      this.mHasCreated = true;
    }

    this.mAttachedScene.anims.play("release", this.sprite);
    this.mAttachedScene.anims.play(
      `releaseBow_${this.GetBowImageKey()}`,
      this.mBowSprite
    );

    this.mPowerArc = new ProgressCircle(this.mAttachedScene, 0, 0, 100);
    this.mPowerArc.Reset();
    this.mAttachedScene.input.on("pointermove", (pointer) => {
      if (this.mAimPointer == null || pointer.id === this.mAimPointer.id) {
        this.mPowerArc.SetPosition(pointer.x, pointer.y);
      }
    });

    this.mExperiencePoints = experiencePoints;
    this.mCharacterAttribs.Strength = this.mCharacterStorage.GetStrength();
    this.mCharacterAttribs.Vitality = this.mCharacterStorage.GetVitality();
    this.mCharacterAttribs.Dexterity = this.mCharacterStorage.GetDexterity();
    this.mCharacterAttribs.Intelligence = this.mCharacterStorage.GetIntelligence();
    this.mCharacterAttribs.AvailableAttributePoints = this.mCharacterStorage.GetAvailableAttributePoints();
    this.mCharacterAttribs.AvailableSkillPoints = this.mCharacterStorage.GetAvailableSkillPoints();

    this.mSkillManager.LoadAllocatedSkills();
    this.mSkillManager.ApplyPassives(this.mCharacterAttribs);

    this.mCharacterAttribs.MaxHealthPoints = AttributeMath.calculateMaxHP(
      this.mExperiencePoints.Level,
      this.mCharacterAttribs.Vitality
    );
    this.mCharacterAttribs.HealthPoints = this.mCharacterAttribs.MaxHealthPoints;

    this.mHealthBar = new BarGui(
      this.mAttachedScene,
      this.sprite.x - 20,
      this.sprite.y - 180,
      this.mCharacterAttribs.MaxHealthPoints
    );

    this.mCharacterAttribs.MaxManaPoints = AttributeMath.calculateMaxMP(
      this.mExperiencePoints.Level,
      this.mCharacterAttribs.Intelligence
    );
    this.mCharacterAttribs.ManaPoints = this.mCharacterAttribs.MaxManaPoints;

    this.mManaBar = new BarGui(
      this.mAttachedScene,
      this.sprite.x - 20,
      this.sprite.y - 160,
      this.mCharacterAttribs.MaxManaPoints
    );
    this.mManaBar.HighColor = 0x0066ff;
    this.mManaBar.LowColor = 0x003388;
    this.mManaBar.Draw();

    this.mFloatingTexts = new FloatingTexts(this.mAttachedScene);

    this.mArrowCaster = new ArrowCaster(
      this.mAttachedScene,
      this.mArrowContainer,
      () => this.mBow,
      () => this.mCharacterAttribs,
      () => ({ x: this.sprite.x, y: this.sprite.y }),
      () => this.mAimPointer ?? this.mAttachedScene.input.activePointer
    );
  }

  public PullArrow() {
    this.mBow.IsPulled = true;
    this.mAttachedScene.anims.play(`shoot_${this.mBow.Name}`, this.sprite);
    this.mAttachedScene.anims.play(
      `shootBow_${this.GetBowImageKey()}`,
      this.mBowSprite
    );
    this.mPullArrowStartTime = new Date();
  }

  public ReleaseArrow() {
    const maxArrowsReached = this.mArrowContainer.GetAll().length >= this.mBow.MaxArrows;

    if (!this.mBow.IsPulled || maxArrowsReached) {
      return;
    }

    this.mAttachedScene.anims.play("release", this.sprite);
    this.mAttachedScene.anims.play(
      `releaseBow_${this.GetBowImageKey()}`,
      this.mBowSprite
    );

    this.CreateArrow(this.mAttachedScene);
    this.mBow.IsPulled = false;
    this.mPowerArc.Reset();
    this.mPullArrowStartTime = null;
  }

  public Update(targetEntities: Array<Monster>) {
    const toKeep: Arrow[] = [];
    const scene = this.mAttachedScene;

    this.mArrowContainer.GetAll().forEach((arrowElem) => {
      let shouldDestroy = false;
      arrowElem.Update();

      targetEntities.forEach((target) => {
        if (arrowElem.HasAlreadyHit(target)) return;
        if (arrowElem.CheckCollision(scene, target.Sprite())) {
          this.mOnArrowHitCallback(arrowElem, target, scene);
          if (arrowElem.IsPiercing) {
            arrowElem.MarkHit(target);
          } else {
            shouldDestroy = true;
          }
        }
      });

      if (!shouldDestroy && arrowElem.IsMoving()) {
        toKeep.push(arrowElem);
      } else {
        arrowElem.destroy();
      }
    });
    this.mArrowContainer.Replace(toKeep);

    targetEntities.forEach((target) => {
      if (target.GetX() < this.sprite.x || this.CheckCollision(target)) {
        const damage = DamageMath.calculateDamageToCharacter(
          target.DamageDealing,
          this.mCharacterAttribs.Vitality
        );
        this.TakeDamage(damage);
        this.mOnDamageCallback(this.mAttachedScene, target, damage);
      }
    });

    if (this.mOnLoseCallback != null && this.mCharacterAttribs.HealthPoints <= 0) {
      this.mOnLoseCallback(this.mAttachedScene);
      this.mOnLoseCallback = null;
    }

    if (this.mPullArrowStartTime != null) {
      const percentagePower = BowMath.calculatePercentagePowerArrow(
        this.mPullArrowStartTime,
        this.mBow.TimeToFullPower,
        this.mCharacterAttribs.Strength
      );
      console.log("PERC: ", percentagePower);
      this.mPowerArc.Update(percentagePower);
    }
  }

  public RegenMana() {
    if (this.mCharacterAttribs.ManaPoints >= this.mCharacterAttribs.MaxManaPoints) {
      return;
    }
    const regenAmount = AttributeMath.calculateManaRegenPerSecond(
      this.mCharacterAttribs.Intelligence
    );
    this.mCharacterAttribs.ManaPoints = Math.min(
      this.mCharacterAttribs.ManaPoints + regenAmount,
      this.mCharacterAttribs.MaxManaPoints
    );
    this.mManaBar.Update(
      this.mCharacterAttribs.ManaPoints,
      this.mCharacterAttribs.MaxManaPoints
    );
  }

  public ConsumeMana(amount: number): boolean {
    if (this.mCharacterAttribs.ManaPoints < amount) {
      return false;
    }
    this.mCharacterAttribs.ManaPoints -= amount;
    this.mManaBar.Update(
      this.mCharacterAttribs.ManaPoints,
      this.mCharacterAttribs.MaxManaPoints
    );
    const spawnX = this.sprite.x + MathHelpers.RandomInteger(-15, 25);
    const spawnY = this.sprite.y - MathHelpers.RandomInteger(80, 160);
    this.mFloatingTexts.AddText(`-${amount}`, spawnX, spawnY, "#44AAFF", "24px");
    return true;
  }

  public GetManaPoints(): number {
    return this.mCharacterAttribs.ManaPoints;
  }

  public GetMaxManaPoints(): number {
    return this.mCharacterAttribs.MaxManaPoints;
  }

  public Destory() {
    this.sprite.destroy();
    this.mBowSprite.destroy();
    this.mManaBar.Destroy();
  }

  public GetSprite() {
    return this.sprite;
  }

  public SetBow(bow: Bow) {
    this.mBow = bow;
    this.mBowSprite = this.mAttachedScene.physics.add.staticSprite(
      80,
      472,
      this.GetBowImageKey(),
      0
    );
  }

  public GetAttributes() {
    return this.mCharacterAttribs;
  }

  public GetSkillManager(): SkillManager {
    return this.mSkillManager;
  }

  public GetArrowCaster(): IArrowCaster {
    return this.mArrowCaster;
  }

  public SetAimPointer(pointer: Phaser.Input.Pointer): void {
    this.mAimPointer = pointer;
  }

  private CheckCollision(monster: Monster) {
    return this.mAttachedScene.physics.overlap(
      this.sprite,
      monster.Sprite(),
      () => {},
      () => {},
      this
    );
  }

  private TakeDamage(finalDamage: number) {
    this.mCharacterAttribs.HealthPoints -= finalDamage;
    this.mHealthBar.Decrease(finalDamage);
  }

  private CreateArrow(scene: Phaser.Scene) {
    const spriteArrow = scene.physics.add.sprite(0, 0, "arrow");
    spriteArrow.setScale(0.5, 0.5);

    const wrapArrow = new Arrow(spriteArrow);
    this.mArrowContainer.Add(wrapArrow);

    const pointer = this.mAimPointer ?? scene.input.activePointer;
    const angle =
      Math.atan2(pointer.x - this.sprite.x, -(pointer.y - this.sprite.y)) *
        (180 / Math.PI) -
      180;

    wrapArrow.Init(angle);

    if (this.mPullArrowStartTime === null) {
      console.warn("Logic error");
      return;
    }

    let percentagePower = BowMath.calculatePercentagePowerArrow(
      this.mPullArrowStartTime,
      this.mBow.TimeToFullPower,
      this.mCharacterAttribs.Strength
    );

    let power = 0;
    if (percentagePower < 0) {
      power = 0.1;
    } else if (percentagePower > 100) {
      power = 1.0;
    } else {
      power = percentagePower / 100;
    }

    const bowDamage = MathHelpers.RandomInteger(
      this.mBow.MinBaseDamage,
      this.mBow.MaxBaseDamage
    );

    const damage: CalculatedDamage = DamageMath.calculateArrowDamage(
      this.mCharacterAttribs.Strength,
      this.mCharacterAttribs.Dexterity,
      bowDamage,
      power
    );

    wrapArrow.Damage = damage.Damage;
    wrapArrow.IsCritical = damage.CriticalHit;

    wrapArrow.ShootArrow(
      pointer.x,
      pointer.y,
      this.sprite.x,
      this.sprite.y,
      power
    );

    spriteArrow.setPosition(this.sprite.x, this.sprite.y);
    spriteArrow.setBounce(0);
    spriteArrow.setCollideWorldBounds(true);
  }

  private GetBowImageKey() {
    return `bow_${this.mBow.Name}`;
  }
}
